// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import { Button } from "react-bootstrap";

import { tabClickHandler, isValidEmail, MIN_PASSWORD_LENGTH } from "guiutils";
import { isSet } from "shared/utils/utils";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeForgotPassword } from "actions";
import { DataState, UserStateAWSError, ForegroundPanel } from "types";
import InputField, { eInputFieldType } from "view/comp/InputField";
import LoginLayout from "view/layout/LoginLayout";
import LoginErrorStatus from "./LoginErrorStatus";

declare const IS_CLOUD: boolean;

interface IUserPwdForgotProps {
  hasPasswordForgetConfirmCode: boolean;
  dataState: DataState;
  saveResult: string | undefined;
  userEmail: string | undefined;
  awsErrorFeedback: UserStateAWSError | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IUserPwdForgotProps = {
    hasPasswordForgetConfirmCode: state.gui.foregroundPanel === ForegroundPanel.PASSWORD_CONFIRM,
    dataState: state.scheduler.loginDataState,
    saveResult: state.gui.login.userStateErrorInfo,
    userEmail: state.gui.login.email,
    awsErrorFeedback: state.gui.login.userStateAWSFeedback,
  };
  return props;
};

interface IUserPwdForgotState {
  username: string;
  confirmationCode: string;
  newPassword: string;
  newPassword2: string;
  showUsernameError: boolean;
  showPasswordError: boolean;
}

interface IUserPwdForgotDispatch {
  onRequestCode: (state: IUserPwdForgotState) => void;
  onChangePassword: (state: IUserPwdForgotState) => void;
  onLogin: (state: IUserPwdForgotState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserPwdForgotDispatch = {
    onRequestCode: (state: IUserPwdForgotState) => {
      const payload: IActionTypeForgotPassword = {
        username: state.username,
        confirmationCode: "",
        newPassword: "",
      };
      dispatch(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CODE_REQ, payload));
    },
    onChangePassword: (state: IUserPwdForgotState) => {
      const payload: IActionTypeForgotPassword = {
        username: state.username,
        confirmationCode: state.confirmationCode,
        newPassword: state.newPassword,
      };
      dispatch(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ, payload));
    },
    onLogin: (state: IUserPwdForgotState) => {
      dispatch(raiseAction(ActionType.ACT_BACK_TO_LOGIN, state.username));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  emailPlaceholder: { id: "e.g. jane@doe.com" },
  verificationCodePlaceholder: { id: "Verification Code" },
  passwordPlaceholder: { id: "min. 8 characters/digits/symbols" },
  passwordsTooShort: { id: "Password too short" },
});

class UserPwdForgotComponent extends React.Component<
  IUserPwdForgotProps & IUserPwdForgotDispatch,
  IUserPwdForgotState
> {
  state: IUserPwdForgotState = {
    username: this.props.userEmail ?? "",
    confirmationCode: "",
    newPassword: "",
    newPassword2: "",
    showUsernameError: false,
    showPasswordError: false,
  };

  public render() {
    return (
      <LoginLayout>
        <div className="pwForgotInfo">
          <h1>
            <FormattedMessage id="Reset Password" />
          </h1>
          {this.props.hasPasswordForgetConfirmCode ?
            <>
              <p>
                <FormattedMessage id="We've sent a verification code to your email address." />
              </p>
              <p>
                <FormattedMessage id="Please enter that code in the input box and choose and verify your new password." />
              </p>
            </>
          : <>
              <p>
                <FormattedMessage id="You can request a verification code to be sent to your email address of your account." />
              </p>
              <p>
                <FormattedMessage id="Afterwards you can enter the received verification code here together with a new password." />
              </p>
            </>
          }
        </div>
        <form onSubmit={(e) => e.preventDefault()}>
          <table className="w-100">
            <tbody>
              {(this.props.userEmail === "" || !this.props.hasPasswordForgetConfirmCode) && (
                <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="username">
                        <FormattedMessage id="Email Address" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Text}
                        name="username"
                        autoComplete="username"
                        value={this.state.username}
                        dataState={this.props.dataState}
                        placeholder={messages.emailPlaceholder}
                        autoFocus
                        errorText={
                          (
                            this.props.dataState === DataState.SAVE_FAIL ||
                            (this.state.showUsernameError && !this.isUsernameValid())
                          ) ?
                            "Check your Email Address"
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ username: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                </>
              )}
              {this.props.hasPasswordForgetConfirmCode && (
                <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="confirmationCode">
                        <FormattedMessage id="Verification Code" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Text}
                        name="confirmationCode"
                        autoComplete="one-time-code"
                        value={this.state.confirmationCode}
                        dataState={this.props.dataState}
                        placeholder={messages.verificationCodePlaceholder}
                        errorText={
                          (
                            (this.props.dataState === DataState.SAVE_FAIL ||
                              this.state.showPasswordError) &&
                            !this.isConfirmationCodeValid()
                          ) ?
                            "ConfirmationCode needed"
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ confirmationCode: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="newPassword">
                        <FormattedMessage id="New password" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Password}
                        name="newPassword"
                        autoComplete="new-password"
                        value={this.state.newPassword}
                        dataState={this.props.dataState}
                        placeholder={messages.passwordPlaceholder}
                        errorText={
                          (
                            this.props.dataState === DataState.SAVE_FAIL ||
                            this.state.showPasswordError
                          ) ?
                            this.state.newPassword.length === 0 ?
                              "Password needed"
                            : this.state.newPassword.length < MIN_PASSWORD_LENGTH ?
                              "Password too short"
                            : this.state.newPassword !== this.state.newPassword2 ?
                              "Passwords are different"
                            : undefined
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ newPassword: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="confirmPassword">
                        <FormattedMessage id="Verify password" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Password}
                        name="confirmPassword"
                        autoComplete="new-password"
                        value={this.state.newPassword2}
                        dataState={this.props.dataState}
                        placeholder={messages.passwordPlaceholder}
                        errorText={
                          (
                            this.props.dataState === DataState.SAVE_FAIL ||
                            this.state.showPasswordError
                          ) ?
                            this.state.newPassword2.length === 0 ?
                              "Password needed"
                            : this.state.newPassword2.length < MIN_PASSWORD_LENGTH ?
                              "Password too short"
                            : this.state.newPassword !== this.state.newPassword2 ?
                              "Passwords are different"
                            : undefined
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ newPassword2: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </form>
        <div className="mt-3">
          <div className="button-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => this.props.onLogin(this.state)}
              disabled={this.props.dataState === DataState.SAVE_REQ}
            >
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              id="reqpwdresetcode"
              type="submit"
              // "Request New Code" is secondary, "Reset Password" the main action
              variant={this.props.hasPasswordForgetConfirmCode ? "primary" : "outline-success"}
              onClick={() => {
                const isPasswordStepValid =
                  !this.props.hasPasswordForgetConfirmCode ||
                  (this.isPasswordValid() && this.isConfirmationCodeValid());
                if (!this.isUsernameValid() || !isPasswordStepValid) {
                  this.setState({
                    showUsernameError: true,
                    showPasswordError: this.props.hasPasswordForgetConfirmCode,
                  });
                  return;
                }
                if (this.props.hasPasswordForgetConfirmCode) {
                  this.props.onChangePassword(this.state);
                } else {
                  this.setState({ confirmationCode: "" });
                  this.props.onRequestCode(this.state);
                }
              }}
              disabled={this.props.dataState === DataState.SAVE_REQ}
            >
              {this.props.hasPasswordForgetConfirmCode ?
                <FormattedMessage id="Reset Password" />
              : <FormattedMessage id="Request New Code" />}
            </Button>
          </div>
          {this.state.showUsernameError && !this.isUsernameValid() && (
            <div className="alert alert-danger mt-3">
              <FormattedMessage id="Please enter a valid email address." />
            </div>
          )}
          {IS_CLOUD &&
            this.props.dataState !== DataState.SAVE_REQ &&
            this.props.hasPasswordForgetConfirmCode &&
            (this.props.awsErrorFeedback === UserStateAWSError.CODE_MISMATCH ||
              this.props.awsErrorFeedback === UserStateAWSError.CODE_MISSING ||
              this.props.awsErrorFeedback === UserStateAWSError.CODE_EXPIRED) && (
              <div className="loginOptions">
                <span
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  className="text-link ms-3"
                  onClick={() => {
                    this.setState({ confirmationCode: "" });
                    this.props.onRequestCode(this.state);
                  }}
                >
                  <FormattedMessage id="Resend Code..." />
                </span>
              </div>
            )}
        </div>
        <div className="mt-4">
          {this.props.dataState === DataState.SAVE_REQ && (
            <div className="alert alert-warning mt-3">
              {this.props.hasPasswordForgetConfirmCode ?
                <FormattedMessage id="Changing password..." />
              : <FormattedMessage id="Requesting verification code..." />}
            </div>
          )}
          {this.props.dataState === DataState.SAVE_FAIL && (
            <div className="alert alert-danger mt-3">
              {this.props.hasPasswordForgetConfirmCode ?
                <FormattedMessage id="Changing password failed." />
              : <FormattedMessage id="Requesting verification code failed." />}
              <br />
              {isSet(this.props.saveResult) && <FormattedMessage id={this.props.saveResult} />}
            </div>
          )}
          {this.props.dataState === DataState.SAVE_FAIL && <LoginErrorStatus />}
          {this.renderPasswordAlert()}
        </div>
      </LoginLayout>
    );
  }

  private renderPasswordAlert() {
    if (!this.state.showPasswordError) {
      return null;
    }
    if (!this.isConfirmationCodeValid()) {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Please enter the verification code." />
        </div>
      );
    }
    if (this.state.newPassword === "" || this.state.newPassword2 === "") {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Please enter a password." />
        </div>
      );
    }
    if (
      this.state.newPassword.length < MIN_PASSWORD_LENGTH ||
      this.state.newPassword2.length < MIN_PASSWORD_LENGTH
    ) {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Your password must be at least 8 characters long." />
        </div>
      );
    }
    if (this.state.newPassword !== this.state.newPassword2) {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="The two fields for the new password do not match." />
        </div>
      );
    }
    return null;
  }

  private isConfirmationCodeValid() {
    return this.state.confirmationCode !== "";
  }

  private isUsernameValid() {
    if (IS_CLOUD) {
      return isValidEmail(this.state.username);
    }
    return true;
  }

  private isPasswordValid() {
    if (IS_CLOUD) {
      return (
        this.state.newPassword.length >= MIN_PASSWORD_LENGTH &&
        this.state.newPassword === this.state.newPassword2
      );
    }
    return true;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserPwdForgotComponent);
